// eslint-disable-next-line no-unused-vars
import React, { forwardRef } from 'react';
import { bool, array, func } from 'prop-types';
import styled from '@emotion/styled';
import { Button, Loading } from '@carbon/react';
import { useTheme } from '@emotion/react';
import { useTranslation } from 'react-i18next';
import { Bell } from 'lucide-react';

import useNumberUtils from 'Hooks/useNumberUtils';

const StyledContainer = styled.div`
  position: relative;
`;

const StyledButton = styled(Button)`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0;
  outline: 0;
  border: 0;
  box-shadow: none !important;
  background-color: transparent !important;
  color: ${ props => props.theme.colors.darkGray };
  font-size: 1rem;

  &:hover {
    color: ${ props => props.theme.colors.darkGray };
  }

  svg {
    margin: 0 5px;
  }

  .items {
    position: absolute;
    top: -4px;
    left: 18px;
    width: 20px;
    height: 20px;
    border-radius: 20px;
    text-align: center;
    line-height: 20px;
    font-size: 0.75rem;
    font-weight: 600;
    background-color: #D52B1E;
    color: ${ props => props.theme.colors.white };
  }

  @media screen and (max-width: ${ props => props.theme.device.tablet }) {
    width: 32px;

    span:not(.items) {
      display: none;
    }
  }

  @media screen and (max-width: ${ props => props.theme.device.mobile }) {
      color: ${ props => props.theme.colors.white };
      font-size: 1rem;

      &:hover {
        color: ${ props => props.theme.colors.white };
      }
  }
`;

const DropdownMenu = styled.div`
  &:after {
    display: block;
    position: absolute;
    top: -2px;
    left: 50%;
    width: 10px;
    height: 10px;
    background-color: ${ props => props.theme.colors.white };
    transform: rotate(45deg) translateX(-50%);
    content: '';
  }

  position: absolute;
  width: 350px;
  top: 50px;
  left: 50%;
  transform: translateX(-50%);
  color: ${ props => props.theme.colors.darkGray };
  background-color: ${ props => props.theme.colors.white };
  z-index: 13;
  box-shadow: 0 3px 6px #00000029;
  opacity: ${ props => props.open ? 1 : 0 };
  visibility: ${ props => props.open ? 'visible' : 'hidden' };
  transition: opacity 0.25s ease-out;

  font-size: 0.875rem;

  p {
    color: inherit;
    &.error {
      padding: 0 16px;
    }
  }

  .title {
    font-weight: 600;
    padding: 0 16px;
    margin: 10px 0;
  }

  ul li {
    &:hover {
      background-color: ${ props => props.theme.colors.shadowGray };
    }

    padding: 0 1rem;
    border-top: 1px solid ${ props => props.theme.colors.shadowGray };
    display: flex;
    flex-direction: column;

    button {
      padding: 10px 0;
      width: 100%;
      border: 0;
      outline: 0;
      background-color: transparent;
      box-shadow: none;
      text-align: left;
      cursor: pointer;
    }

    p {
      margin: 2px 0;
    }

    .name {
      font-size: 0.875rem;
      color: ${ props => props.theme.colors.darkGray }
    }

    .date {
      color: ${ props => props.theme.colors.gray };
      font-size: 0.75rem;
    }
  }

  .buttonContainer {
    display: flex;
    justify-content: center;
    margin: 10px 0 15px;

    button {
      span {
        margin-left: 10px;
      }

      cursor: pointer;
      background-color: transparent;
      width: calc(100% - 2rem);
      height: 50px;
      display: flex;
      align-items: center;
      justify-content: center;
      text-align: center;
      font-size: 1rem;
      color: ${ props => props.theme.colors.blue };
      border: 1px solid ${ props => props.theme.colors.blue };
    }
  }

  hr {
    background-color: ${ props => props.theme.products.colors.border };
  }

  @media screen and (max-width: ${ props => props.theme.device.mobile }) {
    position: fixed;
    inset: 94px 0 0 0;
    width: 100vw;
    height: fit-content;
    transform: translate(0);

    &:after {
      right: 114px;
      left: auto;
    }
  }
`;

const Alerts = forwardRef(({ onToggle, loading, notifications, onOpen, onNavigateToNotifications, open }, ref) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { formatDate } = useNumberUtils();
  // const { data } = useNotifications();
  return (
    <StyledContainer className="alertsSection" ref={ ref }>
      <StyledButton theme={ theme } onClick={ onToggle }>
        { (notifications || []).length > 0 && (
          <span className="items">{ notifications.length }</span>
        ) }
        <Bell size={ 28 } />
      </StyledButton>
      <DropdownMenu theme={ theme } open={ open }>
        <p className="title">{ t('common.notifications') }</p>
        { loading && <Loading active withOverlay={ false } small /> }
        { (!loading && notifications?.length > 0) && (
          <>
            <ul>
              { notifications.map(item => (
                <li key={ item.messageId }>
                  <button
                    type="button"
                    onClick={ () => onOpen(item.messageId) }
                    data-test="onReadMessage"
                    data-testid={ item.messageId }
                  >
                    <p className="name">{ item.title }</p>
                    <p className="date">{ formatDate(item.startDate) }</p>
                  </button>
                </li>
              )) }
            </ul>
            <div className="buttonContainer">
              <button
                onClick={ () => onNavigateToNotifications('notifications') }
                type="button"
              >
                { t('common.seeAllNotifications') } <span>{ '>' }</span>
              </button>
            </div>
          </>
        ) }
        { (!loading && notifications.length === 0) && (
          <p className="error">{ t('common.noNotifications') }</p>
        ) }
      </DropdownMenu>
    </StyledContainer>
  );
});

Alerts.displayName = 'AlertsMenu';

Alerts.propTypes = {
  onToggle: func,
  onOpen: func,
  onNavigateToNotifications: func,
  loading: bool,
  open: bool,
  notifications: array
};

export default Alerts;
